/**
 * PropertyRequestFilters Component
 * Filter controls for property requests
 */

import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select, { type SelectOption } from "../../../components/ui/Select";

interface PropertyRequestFiltersProps {
	filters: {
		status: string;
		request_type: string;
		search: string;
	};
	onFilterChange: (filters: {
		status?: string;
		request_type?: string;
		search?: string;
	}) => void;
}

const PropertyRequestFilters: React.FC<PropertyRequestFiltersProps> = ({
	filters,
	onFilterChange,
}) => {
	const statusOptions: SelectOption[] = [
		{ value: "", label: "All Statuses" },
		{ value: "pending", label: "Pending" },
		{ value: "under_review", label: "Under Review" },
		{ value: "accepted", label: "Accepted" },
		{ value: "declined", label: "Declined" },
		{ value: "expired", label: "Expired" },
	];

	const requestTypeOptions: SelectOption[] = [
		{ value: "", label: "All Types" },
		{ value: "lease", label: "Lease" },
		{ value: "buy", label: "Buy" },
	];

	const handleClearFilters = () => {
		onFilterChange({ status: "", request_type: "", search: "" });
	};

	return (
		<div className="bg-card border border-border rounded-lg p-4 sm:p-6">
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
				{/* Search */}
				<div className="md:col-span-2">
					<Input
						label="Search"
						placeholder="Search by name, email, phone..."
						value={filters.search}
						onChange={(e) => onFilterChange({ search: e.target.value })}
					/>
				</div>

				{/* Status Filter */}
				<Select
					label="Status"
					value={filters.status}
					onValueChange={(value) => {
						const selectedValue = Array.isArray(value) ? value[0] : value;
						onFilterChange({ status: String(selectedValue) });
					}}
					options={statusOptions}
				/>

				{/* Request Type Filter */}
				<Select
					label="Request Type"
					value={filters.request_type}
					onValueChange={(value) => {
						const selectedValue = Array.isArray(value) ? value[0] : value;
						onFilterChange({ request_type: String(selectedValue) });
					}}
					options={requestTypeOptions}
				/>
			</div>

			{/* Clear Filters Button */}
			{(filters.status || filters.request_type || filters.search) && (
				<div className="mt-4 flex justify-end">
					<Button variant="outline" size="sm" onClick={handleClearFilters}>
						<Icon name="X" size={16} className="mr-2" />
						Clear Filters
					</Button>
				</div>
			)}
		</div>
	);
};

export default PropertyRequestFilters;
